'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useForumStore } from '@/store/forumStore';
import {
  SearchInput,
  FilterChips,
  FilterOption,
  ValidationFooter,
  ERROR_BORDER_CLASS,
  CrisisModal,
  InappropriateLanguageModal,
  InappropriateLanguageBanner,
} from '@/components/ui';
import { useContentModeration } from '@/hooks/useContentModeration';
import { MessageSquare, X } from 'lucide-react';
import { GroupTopicCard } from './GroupTopicCard';
import { useLanguage } from '@/context/LanguageContext';
import { ActivityLogger } from '@/lib/activityLog';

interface ForumTabProps {
  groupId: number;
  groupDocumentId: string;
  groupName: string;
  isMember: boolean;
  isGroupAdmin?: boolean;
}

export function ForumTab({ groupId, groupDocumentId, groupName, isMember, isGroupAdmin = false }: ForumTabProps) {
  const { t } = useLanguage();
  const { categories, groupTopics, loading, fetchCategories, fetchGroupTopics, createTopic } = useForumStore();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDetails, setNewDetails] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Content moderation (MWBI-881). Scan the human-text fields only — title +
  // details — joined with "\n"; this form has no URL fields to exclude. The
  // "\n" join stops a banned word from forming across the title/details
  // boundary: the inappropriate detector's \b…\b regex treats "\n" as a
  // non-word char so it can't match across the seam, and no default crisis
  // keyword contains a newline (the crisis detector uses substring includes()).
  // Called above every early return to satisfy the Rules of Hooks. The scanned
  // text is gated on `showModal` so detection stays inert while the
  // Start-Discussion modal is closed but ForumTab is still mounted (the group
  // pages keep ForumTab mounted for as long as the Forum tab is active).
  const moderationText = showModal ? [newTitle, newDetails].join('\n') : '';
  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(moderationText);

  const topics = groupTopics[groupId] || [];
  const topicCount = topics.length;

  useEffect(() => {
    fetchCategories();
    fetchGroupTopics(groupId);
  }, [fetchCategories, fetchGroupTopics, groupId]);

  // Deep-link scroll + highlight + hash cleanup (MWBI-910). Group-activity emails
  // link to /groups/<id>?tab=forum#forum-<topicDocumentId>. Once topics load,
  // scroll the named topic into view, briefly highlight it, and move keyboard/AT
  // focus onto it, then strip the hash so a refresh or tab switch does not
  // re-trigger. Mirrors the page-level effect in frontend/src/app/forums/page.tsx;
  // the matching GroupTopicCard self-expands (child effects run before this parent
  // effect). Differences from the reference, both deliberate: getElementById (not
  // querySelector) tolerates any documentId, focus() lands non-visual users on the
  // topic, and only the hash is cleared so the ?tab=forum query keeping this tab
  // active is preserved.
  useEffect(() => {
    if (typeof window === 'undefined' || topicCount === 0) return;
    const hash = window.location.hash;
    if (!hash.startsWith('#forum-')) return;

    // Track every timer so cleanup can cancel the nested ones too. Otherwise, if
    // the user navigates away after the outer timer fires, the delayed
    // hash-clearing replaceState would run against whatever route is now active
    // and strip its hash (MWBI-910 review follow-up).
    let ringTimer: ReturnType<typeof setTimeout> | undefined;
    let hashTimer: ReturnType<typeof setTimeout> | undefined;
    const scrollTimer = setTimeout(() => {
      const element = document.getElementById(hash.slice(1));
      if (!(element instanceof HTMLElement)) return;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
      element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
      ringTimer = setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
      }, 3000);
      hashTimer = setTimeout(() => {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }, 500);
    }, 500);

    return () => {
      clearTimeout(scrollTimer);
      if (ringTimer) clearTimeout(ringTimer);
      if (hashTimer) clearTimeout(hashTimer);
    };
  }, [topicCount]);

  // Reset produced modal state when the Start-Discussion modal closes. Gating
  // `moderationText` on `showModal` (above) stops new detection, but a modal the
  // hook already opened survives in the still-mounted ForumTab. Crisis is
  // informational and does NOT block submit, so a user can post with the
  // advisory still open; on success the fields clear, and on reopen the advisory
  // would otherwise resurface over the clean form (reachable past the overlay
  // via keyboard / AT). Dismissing both on close closes that gap — the same fix
  // CreateBlogModal applies for its mounted-but-closed modal (MWBI-871 / #485).
  useEffect(() => {
    if (!showModal) {
      dismissCrisisModal();
      dismissInappropriateModal();
    }
  }, [showModal, dismissCrisisModal, dismissInappropriateModal]);

  const filteredTopics = topics
    .filter(topic => {
      const matchesSearch = topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (topic.content?.[0] as { children?: { text?: string }[] })?.children?.[0]?.text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.category?.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !selectedCategorySlug || topic.category?.slug === selectedCategorySlug;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const categoryOptions: FilterOption<string | null>[] = [{ label: t.common.all, value: null }, ...categories.map(cat => ({ label: cat.name, value: cat.slug }))];

  const closeModal = () => {
    setShowModal(false);
    setSubmitAttempted(false);
  };

  const handleAskQuestion = async () => {
    if (!currentUser || !isMember) return;
    if (!newTitle.trim() || !newCategory) {
      setSubmitAttempted(true);
      return;
    }
    // Defense-in-depth for the disabled submit button: keyboard / AT /
    // programmatic callers must hit the same inappropriate-language block.
    // Crisis is intentionally NOT guarded here — it is informational and never
    // blocks posting (mirrors CreateBlogModal / CreateNewsModal).
    if (hasInappropriate) return;
    setSubmitting(true);
    const success = await createTopic({
      title: newTitle.trim(),
      content: newDetails.trim(),
      categoryDocumentId: newCategory,
      groupId,
      groupDocumentId, // Use documentId for stable relation
    });
    if (success) {
      ActivityLogger.createForumTopic(currentUser, newTitle.trim());
      setNewTitle(''); setNewDetails(''); setNewCategory('');
      closeModal();
    }
    setSubmitting(false);
  };

  const titleEmpty = submitAttempted && !newTitle.trim();
  const categoryEmpty = submitAttempted && !newCategory;

  return (
    <div className="space-y-4 relative">
      {!isMember && (
        <div className="absolute inset-0 z-10 flex items-start justify-center pt-20">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-4">
            <h3 className="text-base font-semibold text-black mb-2">{t.groupTabs.mustBeMemberForum}</h3>
            <p className="text-sm text-gray-600">{t.groupTabs.memberBenefits}</p>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="group-forum-tab-modal-root">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6">
            <button onClick={closeModal} data-testid="group-forum-tab-modal-close-btn" className="absolute top-4 right-4 text-foreground-muted hover:text-foreground"><X size={20} /></button>
            <h2 className="text-lg font-semibold text-foreground mb-1">{t.groupTabs.startDiscussion}</h2>
            <p className="text-sm text-foreground-muted mb-4">{t.groupTabs.postToForum.replace('{groupName}', groupName)}</p>
            <CrisisModal
              open={showCrisisModal}
              onClose={dismissCrisisModal}
              testIdPrefix="group-forum-tab-crisis"
            />
            <InappropriateLanguageModal
              open={showInappropriateModal}
              onClose={dismissInappropriateModal}
              testIdPrefix="group-forum-tab-inappropriate"
            />
            {hasInappropriate && (
              <InappropriateLanguageBanner testIdPrefix="group-forum-tab-inappropriate" />
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t.common.title} <span className="text-error">*</span></label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder={t.groupTabs.enterTopicTitle}
                  data-testid="group-forum-tab-modal-title-input"
                  className={`w-full px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:border-primary ${titleEmpty ? ERROR_BORDER_CLASS : 'border-border'}`}
                />
                <ValidationFooter
                  value={newTitle}
                  required
                  showRequiredError={submitAttempted}
                  requiredMessage={t.errors.forumTitleRequired}
                  testIdPrefix="group-forum-tab-modal-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t.common.category} <span className="text-error">*</span></label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  data-testid="group-forum-tab-modal-category-select"
                  className={`w-full px-3 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:border-primary ${categoryEmpty ? ERROR_BORDER_CLASS : 'border-border'}`}
                >
                  <option value="">{t.groupTabs.selectCategory}</option>
                  {categories.map((cat) => <option key={cat.documentId} value={cat.documentId}>{cat.name}</option>)}
                </select>
                <ValidationFooter
                  value={newCategory}
                  required
                  showRequiredError={submitAttempted}
                  requiredMessage={t.errors.forumCategoryRequired}
                  testIdPrefix="group-forum-tab-modal-category"
                />
              </div>
              <div><label className="block text-sm font-medium text-foreground mb-1">{t.groupTabs.details}</label><textarea value={newDetails} onChange={(e) => setNewDetails(e.target.value)} placeholder={t.groupTabs.enterTopicDetails} rows={3} data-testid="group-forum-tab-modal-details-textarea" className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:border-primary resize-none" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={closeModal} data-testid="group-forum-tab-modal-cancel-btn" className="px-4 py-2 text-sm font-medium text-foreground hover:bg-background-secondary rounded-lg transition-colors">{t.common.cancel}</button>
              <button onClick={handleAskQuestion} disabled={submitting || hasInappropriate} data-testid="group-forum-tab-modal-submit-btn" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">{submitting ? t.groupTabs.posting : t.groupTabs.post}</button>
            </div>
          </div>
        </div>
      )}

      <div className={!isMember ? 'blur-sm pointer-events-none select-none' : ''}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground" data-testid="group-forum-tab-heading">{t.groupTabs.discussions}</h2>
          {isMember && <button onClick={() => setShowModal(true)} data-testid="group-forum-tab-ask-question-btn" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium whitespace-nowrap hover:opacity-90 transition">{t.groupTabs.startDiscussion}</button>}
        </div>
        <div className="mb-4" data-testid="group-forum-tab-search-input">
          <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder={t.groupTabs.searchDiscussions} />
        </div>
        {/* FilterChips owns its scrolling row; a wrapper scroll container would clip its arrows. */}
        <div className="mb-4" data-testid="group-forum-tab-category-filter-group">
          <FilterChips options={categoryOptions} selected={selectedCategorySlug} onChange={setSelectedCategorySlug} variant="tier2" scrollPrevLabel={t.common.scrollFiltersLeft} scrollNextLabel={t.common.scrollFiltersRight} />
        </div>

        {loading ? <div className="p-8 text-center text-foreground-muted">{t.common.loading}</div> : filteredTopics.length === 0 ? (
          <div data-testid="group-forum-tab-empty-state" className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
            <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
            <p>{t.groupTabs.noDiscussions.replace('{groupName}', groupName)}</p>
            {isMember && <p className="text-sm mt-1">{t.groupTabs.beFirstToStart}</p>}
          </div>
        ) : (
          <div data-testid="group-forum-tab-topic-cards">
            {filteredTopics.map((topic) => <GroupTopicCard key={topic.id} topic={topic} groupId={groupId} searchQuery={searchQuery} isMember={isMember} isGroupAdmin={isGroupAdmin} />)}
          </div>
        )}
      </div>
    </div>
  );
}

